import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Eye, Award } from 'lucide-react';
import { mockData } from '../data/mock';
import { revealVariants, staggerContainer } from '../utils/motion';
import { useInViewOnce } from '../hooks/useInViewOnce';

const valueIcons = {
  'Integrity': Users,
  'Innovation': Target,
  'Excellence': Eye,
  'Sustainability': Award
};

export const About = () => {
  const [ref, isInView] = useInViewOnce();
  const [processRef, processInView] = useInViewOnce();

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-slate-50" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <motion.div
                variants={revealVariants}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-8"
              >
                <span>About SAX</span>
              </motion.div>

              <motion.h1 
                variants={revealVariants}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Who <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">We Are</span>
              </motion.h1>

              <motion.p 
                variants={revealVariants}
                className="text-xl text-gray-600 leading-relaxed mb-8"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                SAX Project Management Services L.L.C is a premier consulting firm specializing in 
                multi-sector project management and advisory services. Based in Dubai, we deliver 
                excellence across Technology, Mining, Finance, and Strategic Business Advisory.
              </motion.p>

              <motion.div 
                variants={revealVariants}
                className="grid grid-cols-2 gap-6"
              >
                {mockData.stats.slice(0, 4).map((stat, index) => (
                  <div key={index} className="text-center bg-white/80 rounded-2xl p-4 backdrop-blur-sm">
                    <div className="text-3xl font-bold text-[#1F3C88]">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div
              variants={revealVariants}
              className="relative"
            >
              <div className="relative w-full h-96">
                <div className="absolute inset-0 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-3xl transform rotate-3" />
                <div className="absolute inset-4 bg-white rounded-3xl shadow-2xl flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_sax-advisory/artifacts/1bisi1kh_transparent.png"
                    alt="SAX Logo"
                    className="w-48 h-auto"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            ref={ref}
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 lg:grid-cols-3 gap-12"
          >
            {/* Mission */}
            <motion.div variants={revealVariants} className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Target size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To empower organizations through innovative consulting solutions, 
                delivering measurable results that drive sustainable growth and operational excellence.
              </p>
            </motion.div>

            {/* Vision */}
            <motion.div variants={revealVariants} className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Eye size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be the leading multi-sector consulting firm, recognized for our commitment to 
                innovation, integrity, and delivering transformational outcomes for our clients.
              </p>
            </motion.div>

            {/* Values Preview */}
            <motion.div variants={revealVariants} className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-[#1F3C88] via-[#FF5C8A] to-[#FF8C42] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Award size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Values</h3>
              <p className="text-gray-600 leading-relaxed">
                Integrity • Innovation • Excellence • Sustainability - 
                These core values guide every decision and shape our commitment to client success.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Values Detail */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            >
              Our Core Values
            </motion.h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {mockData.values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              return (
                <motion.div
                  key={value.title}
                  variants={revealVariants}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-xl flex items-center justify-center mx-auto mb-6">
                    <IconComponent size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* How We Work Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            ref={processRef}
            variants={staggerContainer}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={revealVariants}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
            >
              How We Work
            </motion.h2>
            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Our proven methodology ensures consistent results and continuous improvement across all projects.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {mockData.process.map((step, index) => (
              <motion.div
                key={step.step}
                variants={revealVariants}
                className="text-center group"
              >
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <span className="text-2xl font-bold text-white">{step.step}</span>
                  </div>
                  {index < mockData.process.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42]" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};